import React, { useEffect, useMemo, useRef, useState } from "react";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import ScreenshotMonitorIcon from "@mui/icons-material/ScreenshotMonitor";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import CameraFrontIcon from "@mui/icons-material/CameraFront";
import CameraRearIcon from "@mui/icons-material/CameraRear";
import UsbIcon from "@mui/icons-material/Usb";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import LensBlurIcon from "@mui/icons-material/LensBlur";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import BuildCircleIcon from "@mui/icons-material/BuildCircle";
import { getServicePhones } from "../api/apiCalls";
import bannerImg from "../assets/service-banner.png";
import appleLogo from "../assets/service/apple.png";
import samsungLogo from "../assets/service/samsung.png";
import xiaomiLogo from "../assets/service/xiaomi.png";
import huaweiLogo from "../assets/service/huawei.png";
import honorLogo from "../assets/service/honor.png";

const BRANDS = [
  { id: "Apple", label: "Apple", logo: appleLogo },
  { id: "Samsung", label: "Samsung", logo: samsungLogo },
  { id: "Xiaomi", label: "Xiaomi", logo: xiaomiLogo },
  { id: "Huawei", label: "Huawei", logo: huaweiLogo },
  { id: "Honor", label: "Honor", logo: honorLogo },
];

const PRICE_LABELS = {
  display: "Дисплеј",
  displayOriginal: "Дисплеј оригинал",
  battery: "Батерија",
  speaker: "Звучник",
  frontCamera: "Предна камера",
  backCamera: "Задна камера",
  connector: "Конектор",
  backGlass: "Задно стакло",
  cameraGlass: "Стакло камера",
};

const normalizeBrand = (value) => (value || "").trim().toLowerCase();

const getServiceIcon = (serviceKey) => {
  switch (serviceKey) {
    case "display":
    case "displayOriginal":
      return <ScreenshotMonitorIcon />;
    case "battery":
      return <BatteryChargingFullIcon />;
    case "speaker":
      return <VolumeUpIcon />;
    case "frontCamera":
      return <CameraFrontIcon />;
    case "backCamera":
      return <CameraRearIcon />;
    case "connector":
      return <UsbIcon />;
    case "backGlass":
      return <CropSquareIcon />;
    case "cameraGlass":
      return <LensBlurIcon />;
    default:
      return <SmartphoneIcon />;
  }
};

function ServicePricesView() {
  const [selectedBrand, setSelectedBrand] = useState("");
  const [phones, setPhones] = useState([]);
  const [selectedPhoneId, setSelectedPhoneId] = useState("");
  const [modelSearch, setModelSearch] = useState("");
  const [selectedServiceKeys, setSelectedServiceKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const totalSectionRef = useRef(null);
  const previousSelectedCountRef = useRef(0);

  useEffect(() => {
    const loadPhones = async () => {
      setLoading(true);
      const data = await getServicePhones();
      setPhones(Array.isArray(data) ? data : []);
      setLoading(false);
    };

    loadPhones();
  }, []);

  const phonesForBrand = useMemo(
    () =>
      phones
        .filter((item) => normalizeBrand(item.brand) === normalizeBrand(selectedBrand))
        .sort((a, b) => a.model.localeCompare(b.model)),
    [phones, selectedBrand]
  );

  const selectedBrandMeta = useMemo(
    () => BRANDS.find((item) => item.id === selectedBrand),
    [selectedBrand]
  );

  const filteredPhonesForBrand = useMemo(() => {
    const term = modelSearch.trim().toLowerCase();
    if (!term) {
      return phonesForBrand;
    }
    return phonesForBrand.filter((item) =>
      (item.model || "").toLowerCase().includes(term)
    );
  }, [phonesForBrand, modelSearch]);

  const selectedPhone = phonesForBrand.find((item) => item._id === selectedPhoneId);

  const visiblePrices = useMemo(
    () =>
      selectedPhone
        ? Object.entries(selectedPhone.prices || {}).filter(
            ([, value]) => value !== null && value !== undefined && value !== ""
          )
        : [],
    [selectedPhone]
  );

  const services = useMemo(
    () =>
      visiblePrices.map(([key, value]) => ({
        key,
        label: PRICE_LABELS[key] || key,
        price: Number(value) || 0,
      })),
    [visiblePrices]
  );

  const totalPrice = useMemo(
    () =>
      selectedServiceKeys.reduce((sum, key) => {
        const service = services.find((item) => item.key === key);
        return sum + (service?.price || 0);
      }, 0),
    [selectedServiceKeys, services]
  );

  const selectedServices = useMemo(
    () => services.filter((item) => selectedServiceKeys.includes(item.key)),
    [services, selectedServiceKeys]
  );

  const totalModels = phones.length;
  const totalBrands = BRANDS.length;

  const handleSelectBrand = (brand) => {
    setSelectedBrand(brand);
    setSelectedPhoneId("");
    setModelSearch("");
    setSelectedServiceKeys([]);
  };

  const handleSelectPhone = (id) => {
    setSelectedPhoneId(id);
    setSelectedServiceKeys([]);
  };

  const handleToggleService = (serviceKey) => {
    setSelectedServiceKeys((prev) =>
      prev.includes(serviceKey)
        ? prev.filter((item) => item !== serviceKey)
        : [...prev, serviceKey]
    );
  };

  const handleBackToBrands = () => {
    setSelectedBrand("");
    setSelectedPhoneId("");
    setModelSearch("");
    setSelectedServiceKeys([]);
  };

  const handleBackToModels = () => {
    setSelectedPhoneId("");
    setModelSearch("");
    setSelectedServiceKeys([]);
  };

  useEffect(() => {
    const previousCount = previousSelectedCountRef.current;
    if (previousCount === 0 && selectedServiceKeys.length > 0 && totalSectionRef.current) {
      totalSectionRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
    previousSelectedCountRef.current = selectedServiceKeys.length;
  }, [selectedServiceKeys]);

  return (
    <div className="service-prices-page">
      <div className="container service-prices-content">
        <section
          className="service-hero-panel"
          style={{ backgroundImage: `url(${bannerImg})` }}
        >
          <div className="service-hero-overlay">
            <div className="service-hero-copy">
              <span className="service-eyebrow">Buzz Mobile Service</span>
              <h1>Сервис цени за твојот уред</h1>
              <p>
                Избери бренд, модел и поправка за да добиеш брз преглед на сервисните
                цени за Apple, Samsung, Xiaomi, Huawei и Honor.
              </p>
            </div>

            <div className="service-hero-stats">
              <div className="service-hero-stat">
                <strong>{totalBrands}</strong>
                <span>брендови</span>
              </div>
              <div className="service-hero-stat">
                <strong>{loading ? "..." : totalModels}</strong>
                <span>достапни модели</span>
              </div>
              <div className="service-hero-stat">
                <strong>{selectedPhone ? services.length : "9+"}</strong>
                <span>типови поправки</span>
              </div>
            </div>
          </div>
        </section>


        {(selectedBrand || selectedPhoneId) && (
          <div className="service-breadcrumbs">
            <button type="button" onClick={handleBackToBrands}>
              Брендови
            </button>
            {selectedBrand && <span>/ {selectedBrand}</span>}
            {selectedPhone && (
              <>
                <span>/</span>
                <button type="button" onClick={handleBackToModels}>
                  Модели
                </button>
              </>
            )}
          </div>
        )}

        {!selectedBrand && (
          <section className="service-section-card">
            <div className="service-section-heading">
              <span className="service-eyebrow">Чекор 1</span>
              <h2>Одбери бренд</h2>
              <p>Започни со брендот за да ги прикажеме достапните модели и сервиси.</p>
            </div>

            <div className="service-brand-tabs">
              {BRANDS.map((brand) => (
                <button
                  key={brand.id}
                  type="button"
                  className="service-brand-card"
                  onClick={() => handleSelectBrand(brand.id)}
                >
                  <span className="service-brand-logo-wrap">
                    <img
                      className="service-brand-logo-img"
                      src={brand.logo}
                      alt={`${brand.label} logo`}
                    />
                  </span>
                  <span className="service-brand-name">{brand.label}</span>
                  
                </button>
              ))}
            </div>
          </section>
        )}

        {selectedBrand && !selectedPhoneId && (
          <div className="service-prices-grid service-prices-grid-detail">
            <section className="service-brand-detail-card">
              <span className="service-eyebrow">Чекор 2</span>
              <span className="service-brand-logo-wrap service-brand-logo-wrap-lg">
                {selectedBrandMeta?.logo ? (
                  <img
                    className="service-brand-logo-img"
                    src={selectedBrandMeta.logo}
                    alt={`${selectedBrandMeta.label} logo`}
                  />
                ) : (
                  <SmartphoneIcon />
                )}
              </span>
              <h3>{selectedBrand}</h3>
              <p>Пребарај модел или избери директно од листата подолу.</p>
              <div className="service-search-wrap">
                <SearchIcon />
                <input
                  type="text"
                  className="service-model-search"
                  placeholder="Пребарај модел..."
                  value={modelSearch}
                  onChange={(event) => setModelSearch(event.target.value)}
                />
              </div>
            </section>

            <section className="service-phone-list-card">
              <div className="service-section-heading service-section-heading--compact">
                <span className="service-eyebrow">Модели</span>
                <h2>Избери уред</h2>
                <p>
                  {loading
                    ? "Се вчитуваат модели..."
                    : `${filteredPhonesForBrand.length} модели`}
                </p>
              </div>

              {loading ? (
                <p>Се вчитува...</p>
              ) : phonesForBrand.length === 0 ? (
                <p>Нема достапни сервис цени за овој бренд.</p>
              ) : filteredPhonesForBrand.length === 0 ? (
                <p>Нема модели за ова пребарување.</p>
              ) : (
                <ul className="service-phone-list">
                  {filteredPhonesForBrand.map((phone) => (
                    <li key={phone._id}>
                      <button
                        type="button"
                        className="service-model-card"
                        onClick={() => handleSelectPhone(phone._id)}
                      >
                        <span className="service-model-icon" aria-hidden="true">
                          <SmartphoneIcon />
                        </span>
                        <span className="service-model-name">{phone.model}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </div>
        )}

        {selectedBrand && selectedPhoneId && (
          <div className="service-prices-grid service-prices-grid-detail">
            <section className="service-brand-detail-card">
              <span className="service-eyebrow">Чекор 3</span>
              <span className="service-brand-logo-wrap service-brand-logo-wrap-lg">
                {selectedBrandMeta?.logo ? (
                  <img
                    className="service-brand-logo-img"
                    src={selectedBrandMeta.logo}
                    alt={`${selectedBrandMeta.label} logo`}
                  />
                ) : (
                  <SmartphoneIcon />
                )}
              </span>
              <h3>{selectedPhone?.model || selectedBrand}</h3>
              <p>Избери една или повеќе поправки за брза пресметка.</p>

              <div className="service-brand-phone-vector">
                <BuildCircleIcon />
              </div>
            </section>

            <section className="service-price-card">
              <div className="service-section-heading service-section-heading--compact">
                <span className="service-eyebrow">Поправки</span>
                <h2>{selectedPhone?.model || selectedBrand}</h2>
                <p>Означи ги сервисите што те интересираат за да ја видиш вкупната цена.</p>
              </div>

              {!selectedPhone ? (
                <p>Невалиден модел.</p>
              ) : services.length === 0 ? (
                <p>Нема внесени цени за овој модел.</p>
              ) : (
                <>
                  <div className="service-price-list service-select-list">
                    {services.map((service) => (
                      <button
                        key={service.key}
                        type="button"
                        className={`service-select-card${
                          selectedServiceKeys.includes(service.key) ? " selected" : ""
                        }`}
                        onClick={() => handleToggleService(service.key)}
                      >
                        <span className="service-price-icon">{getServiceIcon(service.key)}</span>
                        <span className="service-price-row-label">{service.label}</span>
                        <strong className="service-select-price">{service.price} ден.</strong>
                      </button>
                    ))}
                  </div>

                  {selectedServices.length > 0 && (
                    <>
                      <div className="service-total-card" ref={totalSectionRef}>
                        <div className="service-total-top">
                          <span className="service-eyebrow">Избрано</span>
                          <CheckCircleOutlineIcon />
                        </div>
                        <div className="service-total-items">
                          {selectedServices.map((service) => (
                            <div className="service-total-item" key={service.key}>
                              <span>{service.label}</span>
                              <strong>{service.price} ден.</strong>
                            </div>
                          ))}
                        </div>
                        <div className="service-total-header">
                          <span>Вкупно:</span>
                          <strong>{totalPrice} ден.</strong>
                        </div>
                      </div>

                      <div className="service-call-note">
                        <p>Закажете сервис на:</p>
                        <a href="tel:+38976810012">076 810 012</a>
                      </div>
                    </>
                  )}
                </>
              )}
            </section>

            <div className="service-disclaimer">
              Цените може да варираат. За најточни и ажурирани информации, ве молиме
              контактирајте нѐ директно.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ServicePricesView;
